import React, { useState, useMemo } from 'react';
import StatsBar from '../ui/StatsBar';

// ===========================================================================
// HELPER FUNCTIONS
// ===========================================================================

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  });
};

const formatDateTime = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDaysAgo = (date) => {
  if (!date) return 'Never';
  const now = new Date();
  const dateObj = new Date(date);
  const diffMs = now - dateObj;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays}d ago`;
};

const getExpirationClass = (quote) => {
  if (quote.is_expired) return 'status-expired';
  if (quote.is_expiring_soon) return 'status-expiring';
  return '';
};

const getExpirationLabel = (quote) => {
  if (quote.is_expired) return 'Expired';
  if (quote.is_expiring_soon) return `Expires in ${quote.days_until_expiry}d`;
  if (quote.days_until_expiry !== null) return `${quote.days_until_expiry}d left`;
  return '-';
};

const getSyncStatusLabel = (status) => {
  const labels = {
    'synced': '✓ Synced',
    'success': '✓ Synced',
    'pending': '⏳ Pending',
    'error': '✕ Error'
  };
  return labels[status] || '⏳ Pending';
};

const getAutotaskOpportunityUrl = (opportunityId) => {
  return `https://ww29.autotask.net/Mvc/CRM/OpportunityDetail.mvc?ids%5B0%5D=${opportunityId}&OpportunityId=${opportunityId}`;
};

const getHubSpotQuoteUrl = (quoteId) => {
  return `https://app.hubspot.com/contacts/23367316/quote/${quoteId}`;
};

const getAutotaskQuoteUrl = (quoteId) => {
  return `https://ww29.autotask.net/Mvc/CRM/QuoteDetail.mvc?ids%5B0%5D=${quoteId}&QuoteId=${quoteId}`;
};

const getN8nExecutionUrl = (workflowId, executionId) => {
  return `https://flow.ccassistant.app/workflow/${workflowId}/executions/${executionId}`;
};

// ===========================================================================
// MAIN COMPONENT
// ===========================================================================

const Quotes = ({ quotes, stats, loading, pagination, fetchQuotes, apiFetch }) => {
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [syncHistory, setSyncHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [quoteItems, setQuoteItems] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);

  const [filters, setFilters] = useState({
    syncStatus: 'all',
    expirationFilter: 'all',
    minAmount: '',
    maxAmount: '',
    searchTerm: ''
  });

  const [sortConfig, setSortConfig] = useState({
    field: 'last_sync_at',
    direction: 'desc'
  });

  // Pagination state - synced with server
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // ===========================================================================
  // FILTERING LOGIC
  // ===========================================================================

  const filteredQuotes = useMemo(() => {
    return quotes.filter(quote => {
      // Search term
      if (filters.searchTerm) {
        const search = filters.searchTerm.toLowerCase();
        const matches =
          quote.name?.toLowerCase().includes(search) ||
          quote.company_name?.toLowerCase().includes(search) ||
          quote.opportunity_title?.toLowerCase().includes(search) ||
          String(quote.autotask_id).includes(search);
        if (!matches) return false;
      }

      // Sync status filter
      if (filters.syncStatus !== 'all' && quote.sync_status !== filters.syncStatus) {
        return false;
      }

      // Expiration filter
      if (filters.expirationFilter !== 'all') {
        switch (filters.expirationFilter) {
          case 'expired':
            if (!quote.is_expired) return false;
            break;
          case 'expiring':
            if (!quote.is_expiring_soon) return false;
            break;
          case 'active':
            if (quote.is_expired) return false;
            break;
          default:
            break;
        }
      }

      // Amount range
      const amount = quote.total_amount || 0;
      if (filters.minAmount && amount < Number(filters.minAmount)) {
        return false;
      }
      if (filters.maxAmount && amount > Number(filters.maxAmount)) {
        return false;
      }

      return true;
    });
  }, [quotes, filters]);

  // ===========================================================================
  // SORTING LOGIC
  // ===========================================================================

  const sortedQuotes = useMemo(() => {
    return [...filteredQuotes].sort((a, b) => {
      let aVal = a[sortConfig.field];
      let bVal = b[sortConfig.field];

      // Handle nulls
      if (!aVal && aVal !== 0) return 1;
      if (!bVal && bVal !== 0) return -1;

      // Type-specific comparison
      if (sortConfig.field === 'total_amount' || sortConfig.field === 'subtotal') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else if (sortConfig.field.includes('date') || sortConfig.field.includes('sync_at')) {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      const comparison = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [filteredQuotes, sortConfig]);

  // ===========================================================================
  // EVENT HANDLERS
  // ===========================================================================

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortArrow = (field) => {
    if (sortConfig.field !== field) return '';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  // ===========================================================================
  // PAGINATION LOGIC (Server-side)
  // ===========================================================================

  const totalPages = pagination?.total_pages || 1;

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchQuotes(page, itemsPerPage);
  };

  const handleItemsPerPageChange = (newPerPage) => {
    setItemsPerPage(newPerPage);
    setCurrentPage(1);
    fetchQuotes(1, newPerPage);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const handleViewDetails = (quote) => {
    setSelectedQuote(quote);
    setShowDetailsModal(true);
    setSyncHistory(null);
    setQuoteItems(null);
  };

  const closeModal = () => {
    setShowDetailsModal(false);
    setSelectedQuote(null);
    setSyncHistory(null);
    setQuoteItems(null);
  };

  const loadSyncHistory = async (quoteId) => {
    setLoadingHistory(true);
    try {
      const response = await apiFetch(`/quotes/${quoteId}/history`);
      const result = await response.json();
      if (result.success) {
        setSyncHistory(result.history);
      }
    } catch (error) {
      console.error('Error loading sync history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadQuoteItems = async (quoteId) => {
    setLoadingItems(true);
    try {
      const response = await apiFetch(`/quotes/${quoteId}/items`);
      const result = await response.json();
      if (result.success) {
        setQuoteItems(result);
      }
    } catch (error) {
      console.error('Error loading quote items:', error);
    } finally {
      setLoadingItems(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      syncStatus: 'all',
      expirationFilter: 'all',
      minAmount: '',
      maxAmount: '',
      searchTerm: ''
    });
  };

  // ===========================================================================
  // ENHANCED STATS
  // ===========================================================================

  const enhancedStats = {
    ...stats,
    entityType: 'Quotes',
    customStats: [
      { label: 'Total Value', value: formatCurrency(stats.totalValue), color: '#10B981' },
      { label: 'Avg Quote', value: formatCurrency(stats.averageValue), color: '#8B5CF6' },
      { label: 'Sync Coverage', value: `${stats.syncCoverage}%`, color: stats.syncCoverage > 90 ? '#10B981' : '#F59E0B' },
      { label: 'Expiring Soon', value: stats.expiringSoon, color: stats.expiringSoon > 0 ? '#F59E0B' : '#6B7280' },
      { label: 'Expired', value: stats.expired, color: stats.expired > 0 ? '#DC2626' : '#6B7280' }
    ]
  };

  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (
    <>
      {/* Stats Bar */}
      <StatsBar stats={enhancedStats} />

      {/* Filters Section */}
      <div className="filters-section">
        <input
          type="text"
          className="search-bar"
          placeholder="🔍 Search by name, company, opportunity, or ID..."
          value={filters.searchTerm}
          onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
        />

        <div className="filter-row">
          <select
            className="filter-select"
            value={filters.syncStatus}
            onChange={(e) => setFilters({...filters, syncStatus: e.target.value})}
          >
            <option value="all">All Sync Status</option>
            <option value="success">Synced</option>
            <option value="pending">Pending</option>
            <option value="error">Error</option>
          </select>

          <select
            className="filter-select"
            value={filters.expirationFilter}
            onChange={(e) => setFilters({...filters, expirationFilter: e.target.value})}
          >
            <option value="all">All Expiration</option>
            <option value="active">Active</option>
            <option value="expiring">Expiring Soon</option>
            <option value="expired">Expired</option>
          </select>

          <input
            type="number"
            className="filter-select"
            placeholder="Min Amount"
            value={filters.minAmount}
            onChange={(e) => setFilters({...filters, minAmount: e.target.value})}
          />

          <input
            type="number"
            className="filter-select"
            placeholder="Max Amount"
            value={filters.maxAmount}
            onChange={(e) => setFilters({...filters, maxAmount: e.target.value})}
          />

          <button
            className="btn-secondary"
            onClick={clearFilters}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className="table-section">
        <div className="table-header">
          <div className="table-title">
            {loading
              ? 'Loading...'
              : `Showing ${sortedQuotes.length} of ${pagination?.total || stats.total} quotes (Page ${currentPage} of ${totalPages})`}
          </div>
          <div className="table-actions">
            <button className="icon-btn-small" onClick={() => fetchQuotes(currentPage, itemsPerPage)}>
              🔄 Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            Loading quotes...
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} className="sortable-header">
                  Quote {sortArrow('name')}
                </th>
                <th onClick={() => handleSort('total_amount')} className="sortable-header">
                  Amount {sortArrow('total_amount')}
                </th>
                <th>Opportunity</th>
                <th>Company</th>
                <th>HubSpot</th>
                <th onClick={() => handleSort('last_sync_at')} className="sortable-header">
                  Last Synced {sortArrow('last_sync_at')}
                </th>
                <th>Sync Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedQuotes.map((quote, index) => (
                <tr key={quote.id || index}>
                  <td>
                    <span onClick={() => handleViewDetails(quote)} className="company-link">
                      {quote.name || `Quote #${quote.autotask_id}`}
                    </span>
                  </td>
                  <td className="amount-cell">
                    {formatCurrency(quote.total_amount)}
                  </td>
                  <td>
                    {quote.opportunity_id ? (
                      <a
                        href={getAutotaskOpportunityUrl(quote.opportunity_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="autotask-link"
                      >
                        {quote.opportunity_title || `#${quote.opportunity_id}`}
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{quote.company_name}</td>
                  <td>
                    {quote.hubspot_quote_id ? (
                      <a
                        href={getHubSpotQuoteUrl(quote.hubspot_quote_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="autotask-link"
                      >
                        View
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {formatDaysAgo(quote.last_sync_at)}
                  </td>
                  <td>
                    <span className={`status-badge ${quote.sync_status}`}>
                      {getSyncStatusLabel(quote.sync_status)}
                    </span>
                  </td>
                  <td>
                    <button
                      className="action-btn"
                      onClick={() => handleViewDetails(quote)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination Controls */}
        {!loading && pagination && (
          <div className="pagination">
            <div className="pagination-info">
              Page {currentPage} of {totalPages} ({pagination.total} total items)
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                style={{ marginLeft: 12, padding: '4px 8px', borderRadius: 6 }}
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={200}>200 per page</option>
              </select>
            </div>
            <div className="pagination-controls">
              <button
                className="page-btn"
                onClick={() => handlePageChange(1)}
                disabled={!pagination.has_prev}
              >
                First
              </button>
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.has_prev}
              >
                Prev
              </button>
              {getPageNumbers().map((page, idx) => (
                page === '...' ? (
                  <span key={`ellipsis-${idx}`} style={{ padding: '10px 8px' }}>...</span>
                ) : (
                  <button
                    key={page}
                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                )
              ))}
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.has_next}
              >
                Next
              </button>
              <button
                className="page-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={!pagination.has_next}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedQuote && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedQuote.name || `Quote #${selectedQuote.autotask_id}`}</h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>

            <div className="modal-body">
              {/* Quote Details Section */}
              <div className="detail-section">
                <h3>Quote Details</h3>
                <div className="detail-row">
                  <strong>Status:</strong>{' '}
                  <span className="status-badge">
                    {selectedQuote.status_label}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Effective Date:</strong> {formatDate(selectedQuote.effective_date)}
                </div>
                <div className="detail-row">
                  <strong>Expiration Date:</strong>{' '}
                  <span className={getExpirationClass(selectedQuote)}>
                    {formatDate(selectedQuote.expiration_date)} ({getExpirationLabel(selectedQuote)})
                  </span>
                </div>
                {selectedQuote.description && (
                  <div className="detail-row">
                    <strong>Description:</strong> {selectedQuote.description}
                  </div>
                )}
              </div>

              {/* Financial Details Section */}
              <div className="detail-section">
                <h3>Financial Details</h3>
                <div className="detail-row">
                  <strong>Subtotal:</strong> {formatCurrency(selectedQuote.subtotal)}
                </div>
                <div className="detail-row">
                  <strong>Tax:</strong> {formatCurrency(selectedQuote.tax_amount)}
                </div>
                <div className="detail-row">
                  <strong>Total Amount:</strong>{' '}
                  <span style={{ fontWeight: 'bold', color: '#10B981' }}>
                    {formatCurrency(selectedQuote.total_amount)}
                  </span>
                </div>
              </div>

              {/* Relationships Section */}
              <div className="detail-section">
                <h3>Relationships</h3>
                <div className="detail-row">
                  <strong>Opportunity:</strong>{' '}
                  {selectedQuote.opportunity_id ? (
                    <a
                      href={getAutotaskOpportunityUrl(selectedQuote.opportunity_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="autotask-link"
                    >
                      {selectedQuote.opportunity_title || `#${selectedQuote.opportunity_id}`}
                    </a>
                  ) : (
                    '-'
                  )}
                </div>
                <div className="detail-row">
                  <strong>Company:</strong> {selectedQuote.company_name}
                </div>
                {selectedQuote.contact_name && (
                  <div className="detail-row">
                    <strong>Contact:</strong> {selectedQuote.contact_name}
                  </div>
                )}
              </div>

              {/* Line Items Section */}
              <div className="detail-section">
                <h3>Line Items</h3>
                {!quoteItems ? (
                  <button
                    className="btn-secondary"
                    onClick={() => loadQuoteItems(selectedQuote.autotask_id)}
                    disabled={loadingItems}
                  >
                    {loadingItems ? 'Loading...' : 'Load Line Items'}
                  </button>
                ) : (
                  <div>
                    {quoteItems.items.length === 0 ? (
                      <div style={{padding: 12, textAlign: 'center', color: '#6B7280'}}>
                        No line items
                      </div>
                    ) : (
                      <>
                        <table className="line-items-table">
                          <thead>
                            <tr>
                              <th>Item</th>
                              <th>Qty</th>
                              <th>Unit Price</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quoteItems.items.map((item, idx) => (
                              <tr key={idx}>
                                <td>{item.name || 'Unnamed Item'}</td>
                                <td>{item.quantity}</td>
                                <td>{formatCurrency(item.unit_price)}</td>
                                <td>{formatCurrency(item.total_price)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div style={{ marginTop: 8, textAlign: 'right', fontWeight: 'bold' }}>
                          Total: {formatCurrency(quoteItems.summary.totalPrice)} ({quoteItems.summary.count} items)
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* System Links Section */}
              <div className="detail-section">
                <h3>System Links</h3>
                <div className="detail-row">
                  <strong>Autotask ID:</strong>{' '}
                  <a
                    href={getAutotaskQuoteUrl(selectedQuote.autotask_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="autotask-link"
                  >
                    {selectedQuote.autotask_id}
                  </a>
                </div>
                <div className="detail-row">
                  <strong>HubSpot Quote:</strong>{' '}
                  {selectedQuote.hubspot_quote_id ? (
                    <a
                      href={getHubSpotQuoteUrl(selectedQuote.hubspot_quote_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="autotask-link"
                    >
                      {selectedQuote.hubspot_quote_id}
                    </a>
                  ) : (
                    'Not synced'
                  )}
                </div>
              </div>

              {/* Sync Status Section */}
              <div className="detail-section">
                <h3>Sync Status</h3>
                <div className="detail-row">
                  <strong>Status:</strong>{' '}
                  <span className={`status-badge ${selectedQuote.sync_status}`}>
                    {getSyncStatusLabel(selectedQuote.sync_status)}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Last Synced:</strong> {formatDateTime(selectedQuote.last_sync_at)}
                </div>

                {selectedQuote.sync_message && (
                  <div className={
                    selectedQuote.sync_status === 'error'
                      ? 'error-message-box'
                      : 'success-message-box'
                  }>
                    {selectedQuote.sync_message}
                  </div>
                )}
              </div>

              {/* Sync History Section */}
              <div className="detail-section">
                <h3>Sync History</h3>
                {!syncHistory ? (
                  <button
                    className="btn-secondary"
                    onClick={() => loadSyncHistory(selectedQuote.autotask_id)}
                    disabled={loadingHistory}
                  >
                    {loadingHistory ? 'Loading...' : 'Load History'}
                  </button>
                ) : (
                  <div className="sync-history">
                    {syncHistory.length === 0 ? (
                      <div style={{padding: 12, textAlign: 'center', color: '#6B7280'}}>
                        No sync history available
                      </div>
                    ) : (
                      syncHistory.map((entry, idx) => (
                        <div key={idx} className="sync-log-entry">
                          <div>
                            <span className={`status-badge ${entry.sync_status}`}>
                              {entry.sync_status}
                            </span>
                            {entry.message && (
                              <div className="text-muted" style={{marginTop: 4, fontSize: 12}}>
                                {entry.message}
                              </div>
                            )}
                          </div>
                          <div className="sync-log-time">
                            <span className="text-muted" style={{fontSize: 12}}>
                              {formatDateTime(entry.created_at)}
                            </span>
                            {entry.execution_id && entry.workflow_id && (
                              <>
                                <span className="text-muted" style={{fontSize: 12}}> • </span>
                                <a
                                  href={getN8nExecutionUrl(entry.workflow_id, entry.execution_id)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="execution-link"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View Execution
                                </a>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Quotes;
